/**
 * Coordinate mapping from detection space to screen space.
 *
 * Handles the object-fit: cover transformation where the video
 * fills the screen container, potentially cropping edges.
 */

import type { Point2D } from '../detection/types';

export interface CoordinateMapperParams {
  videoWidth: number;
  videoHeight: number;
  screenWidth: number;
  screenHeight: number;
  detectionWidth: number;
  detectionHeight: number;
}

export interface CoordinateMapper {
  /** Map a detection-space point to screen-space */
  toScreen(point: Point2D): Point2D;
}

/**
 * Create a coordinate mapper for the current video/screen geometry.
 *
 * The mapping pipeline:
 * 1. Detection coords -> Video coords (scale up from detection resolution)
 * 2. Video coords -> Screen coords (object-fit: cover transform)
 */
export function createCoordinateMapper(params: CoordinateMapperParams): CoordinateMapper {
  const { videoWidth, videoHeight, screenWidth, screenHeight, detectionWidth, detectionHeight } =
    params;

  // Scale from detection resolution to video resolution
  const detScaleX = videoWidth / detectionWidth;
  const detScaleY = videoHeight / detectionHeight;

  // Object-fit: cover calculations
  // The video fills the screen, maintaining aspect ratio, cropping overflow
  const videoAspect = videoWidth / videoHeight;
  const screenAspect = screenWidth / screenHeight;

  let displayWidth: number;
  let displayHeight: number;
  let offsetX: number;
  let offsetY: number;

  if (videoAspect > screenAspect) {
    // Video is wider than screen — height fills, width overflows
    displayHeight = screenHeight;
    displayWidth = screenHeight * videoAspect;
    offsetX = (screenWidth - displayWidth) / 2;
    offsetY = 0;
  } else {
    // Video is taller than screen — width fills, height overflows
    displayWidth = screenWidth;
    displayHeight = screenWidth / videoAspect;
    offsetX = 0;
    offsetY = (screenHeight - displayHeight) / 2;
  }

  const screenScaleX = displayWidth / videoWidth;
  const screenScaleY = displayHeight / videoHeight;

  return {
    toScreen(point: Point2D): Point2D {
      // Step 1: Detection coords -> Video coords
      const vx = point.x * detScaleX;
      const vy = point.y * detScaleY;

      // Step 2: Video coords -> Screen coords
      const sx = offsetX + vx * screenScaleX;
      const sy = offsetY + vy * screenScaleY;

      return { x: sx, y: sy };
    },
  };
}
